import type { Input as RtgInput } from "@revotech-group/revotech-ui-kit";
import { html, LitElement, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import { genRandSeed } from "@/utils";

import {
  PHONE_FIELD_DEFAULT_PROPS as DEFAULT_PROPS,
  genFormatError,
  genRequiredError,
} from "./field.defs";
import type {
  PhoneFieldAutocomplete,
  PhoneFieldPart,
  PhoneFieldValidateType,
  PhoneFieldValidation,
  SbPhoneFieldProps as Props,
} from "./field.types";

/**
 * Self-validating phone input field for use in auth widget forms.
 *
 * @part phone-field - rtg-field: root
 * @part phone-field-label - rtg-field-label
 * @part phone-field-mark - span: decorative marker text/caret in label
 * @part phone-field-description - rtg-field-description
 * @part phone-field-input - rtg-input
 * @part phone-field-error - rtg-field-error: inline validation error message
 *
 * @event sb-phone-field:validate - Fired when validation runs, on form submit or via the public `validate()` method.
 *  Detail: `{ id: string; inputId: string; name: string; value: string; valid: boolean; type: PhoneFieldValidateType; message: string }`
 *    - type: `"success" | "required" | "format"`
 */
@customElement("sb-phone-field")
export class SbPhoneField extends LitElement implements Props {
  static readonly ROOT = "phone-field";
  static readonly LABEL = "label";
  static readonly MARK = "mark";
  static readonly DESCRIPTION = "description";
  static readonly INPUT = "input";
  static readonly ERROR = "error";

  static readonly TAG = `${NAMESPACE}-${SbPhoneField.ROOT}`;

  static readonly VALIDATE_EVENT = `${SbPhoneField.TAG}:validate`;

  static readonly PARTS: Record<PhoneFieldPart, string> = {
    root: SbPhoneField.ROOT,
    label: `${SbPhoneField.ROOT}-${SbPhoneField.LABEL}`,
    mark: `${SbPhoneField.ROOT}-${SbPhoneField.MARK}`,
    description: `${SbPhoneField.ROOT}-${SbPhoneField.DESCRIPTION}`,
    input: `${SbPhoneField.ROOT}-${SbPhoneField.INPUT}`,
    error: `${SbPhoneField.ROOT}-${SbPhoneField.ERROR}`,
  };

  static readonly INPUT_SELECTORS = `rtg-input[part="${SbPhoneField.PARTS.input}"]`;

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * ID given to the input part, forwarded to its inner input element.
   */
  private _inputId?: string;

  private get _defaultInputId() {
    return `${this.rootId}-${SbPhoneField.INPUT}`;
  }

  @property({ type: String, attribute: "input-id" })
  get inputId(): string {
    return this._inputId ?? this._defaultInputId;
  }

  set inputId(value: string | null | undefined) {
    const old = this.inputId;
    this._inputId = value || undefined;
    this.requestUpdate("inputId", old);
  }

  /**
   * HTML `name` attribute forwarded to the inner input, used as the form data
   * key on submission.
   */
  private _name = DEFAULT_PROPS.name;

  @property({ type: String })
  get name() {
    return this._name;
  }

  set name(value: string) {
    const old = this._name;
    this._name = value.length ? value : DEFAULT_PROPS.name;
    this.requestUpdate("name", old);
  }

  /**
   * Pre-fills the input until the user types and their input takes over.
   * Resetting the form restores this value.
   */
  @property({ type: String, attribute: "default-value" })
  defaultValue?: string;

  /**
   * Text used in the label part of the field.
   */
  private _label = DEFAULT_PROPS.label;

  @property({ type: String })
  get label() {
    return this._label;
  }

  set label(value: string) {
    const old = this._label;
    this._label = value.length ? value : DEFAULT_PROPS.label;
    this.requestUpdate("label", old);
  }

  /**
   * Placeholder text shown inside the input when empty.
   */
  @property({ type: String })
  placeholder?: string;

  /**
   * Text used in the description part of the field.
   */
  @property({ type: String })
  description?: string;

  /**
   * Custom text used in the mark part of the field label.
   */
  @property({ type: String })
  mark?: string;

  /**
   * When provided with `required`, the mark part is rendered with an asterisk.
   * Has no effect if a custom `mark` text is provided.
   */
  @property({ type: Boolean })
  caret?: boolean;

  /**
   * HTML `autocomplete` hint forwarded to the inner input element.
   */
  @property({ type: String })
  autocomplete?: PhoneFieldAutocomplete;

  /**
   * Disables the field and its inner input element, preventing interaction.
   */
  @property({ type: Boolean })
  disabled?: boolean;

  /**
   * Marks the field as required: validation fails if field is empty.
   */
  @property({ type: Boolean })
  required?: boolean;

  /**
   * Regex pattern used to validate the phone format.
   */
  @property({ type: String })
  pattern = DEFAULT_PROPS.pattern;

  /**
   * Controls validation depth, only enforcing the required check if `"simple"`.
   */
  @property({ type: String })
  validation: PhoneFieldValidation = DEFAULT_PROPS.validation;

  /**
   * Error message used when the field is required but empty.
   */
  private _requiredError?: string;

  private get _defaultRequiredError() {
    return genRequiredError(this.label);
  }

  @property({ type: String, attribute: "required-error" })
  get requiredError(): string {
    return this._requiredError ?? this._defaultRequiredError;
  }

  set requiredError(value: string | null | undefined) {
    const old = this.requiredError;
    this._requiredError = value || undefined;
    this.requestUpdate("requiredError", old);
  }

  /**
   * Error message used when the field does not satisfy phone format rules.
   */
  private _formatError?: string;

  private get _defaultFormatError() {
    return genFormatError(this.label);
  }

  @property({ type: String, attribute: "format-error" })
  get formatError(): string {
    return this._formatError ?? this._defaultFormatError;
  }

  set formatError(value: string | null | undefined) {
    const old = this.formatError;
    this._formatError = value || undefined;
    this.requestUpdate("formatError", old);
  }

  /* ---------------------------------------- */

  /**
   * Tracks whether the field is currently in an invalid state.
   */
  @state()
  private _invalid = false;

  /**
   * Error message displayed in the error part when the field is invalid.
   */
  @state()
  private _error = "";

  /**
   * Raw value of the phone field input, synced on each input event.
   */
  @state()
  private _value = "";

  /* ---------------------------------------- */

  /**
   * Gates whether `defaultValue` or `_value` is authoritative for `this.value`.
   * True once the user has typed into the input.
   */
  private _dirty = false;

  /**
   * Reference to the nearest `<form>` ancestor.
   */
  private _form: HTMLFormElement | null = null;

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbPhoneField.TAG}-${this.seed}`;
  }

  get labelId() {
    return `${this.rootId}-${SbPhoneField.LABEL}`;
  }

  get markId() {
    return `${this.rootId}-${SbPhoneField.MARK}`;
  }

  get descriptionId() {
    return `${this.rootId}-${SbPhoneField.DESCRIPTION}`;
  }

  get errorId() {
    return `${this.rootId}-${SbPhoneField.ERROR}`;
  }

  /**
   * Normalized value of the phone field input.
   */
  get value() {
    const defaultValue = this.defaultValue ?? "";
    const value = this._dirty ? this._value : defaultValue;

    return value.replace(/\s/g, "");
  }

  /* ------------------------------------------------------------ */

  updated(changed: PropertyValues) {
    if (changed.has("defaultValue") && !this._dirty) {
      const selectors = SbPhoneField.INPUT_SELECTORS;
      const input = this.querySelector(selectors) as RtgInput | null;

      if (input) input.value = this.defaultValue ?? "";
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("input", this._handleInput);

    this._form = this.closest("form");
    if (this._form === null) return;

    this._form.noValidate = true;
    this._form.addEventListener("submit", this._handleFormSubmit);
    this._form.addEventListener("reset", this._handleFormReset);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("input", this._handleInput);

    if (this._form === null) return;

    this._form.removeEventListener("submit", this._handleFormSubmit);
    this._form.removeEventListener("reset", this._handleFormReset);
    this._form = null;
  }

  /* ------------------------------------------------------------ */

  /**
   * Triggers validation and returns `true` if the current value is valid,
   * `false` otherwise.
   */
  validate() {
    return this._validate();
  }

  /* ---------------------------------------- */

  private _dispatchValidate(
    valid: boolean,
    type: PhoneFieldValidateType,
    message: string,
  ) {
    const { rootId: id, inputId, name, value } = this;

    this.dispatchEvent(
      new CustomEvent(SbPhoneField.VALIDATE_EVENT, {
        detail: { id, inputId, name, value, valid, type, message },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onValidateSuccess() {
    this._invalid = false;
    this._error = "";
    this._dispatchValidate(true, "success", "Valid phone.");
  }

  private _onValidateError(type: PhoneFieldValidateType, message: string) {
    this._invalid = true;
    this._error = message;
    this._dispatchValidate(false, type, message);
  }

  private _validate() {
    const value = this.value;

    if (this.required && !value) {
      this._onValidateError("required", this.requiredError);
      return false;
    }

    if (!value || this.validation === "simple") {
      this._onValidateSuccess();
      return true;
    }

    const regExp = new RegExp(this.pattern);
    if (!regExp.test(value)) {
      this._onValidateError("format", this.formatError);
      return false;
    }

    this._onValidateSuccess();
    return true;
  }

  /* ------------------------------------------------------------ */

  private _handleInput = (e: Event) => {
    const target = e.target as HTMLInputElement;

    const selectors = `${SbPhoneField.INPUT_SELECTORS} > input`;

    if (target.matches(selectors)) {
      this._dirty = true;
      this._value = target.value;
    }
  };

  private _handleFormSubmit = (e: Event) => {
    const valid = this._validate();
    if (!valid) e.preventDefault();
  };

  private _handleFormReset = () => {
    const selectors = SbPhoneField.INPUT_SELECTORS;
    const input = this.querySelector(selectors) as RtgInput | null;

    if (input) input.value = this.defaultValue ?? "";

    this._invalid = false;
    this._error = "";
    this._value = "";
    this._dirty = false;
  };

  /* ------------------------------------------------------------ */

  private _renderMark() {
    let content: string;

    if (this.mark) {
      content = "mark";
    } else if (this.required && this.caret) {
      content = "caret";
    } else {
      return nothing;
    }

    return html`
      <span
        id="${this.markId}"
        part="${SbPhoneField.PARTS.mark}"
        data-content="${content}"
      >
        ${this.mark ?? html`&ast;`}
      </span>
    `;
  }

  /* ---------------------------------------- */

  render() {
    return html`
      <rtg-field
        id="${this.rootId}"
        part="${SbPhoneField.PARTS.root}"
        ?disabled=${this.disabled}
        ?invalid=${this._invalid}
      >
        <rtg-field-label
          id="${this.labelId}"
          part="${SbPhoneField.PARTS.label}"
          for="${this.inputId}"
        >
          ${this.label} ${this._renderMark()}
        </rtg-field-label>

        <rtg-input
          id="${this.inputId}"
          part="${SbPhoneField.PARTS.input}"
          type="tel"
          name="${this.name}"
          placeholder="${this.placeholder ?? nothing}"
          autocomplete="${this.autocomplete ?? nothing}"
          ?disabled=${this.disabled}
          ?invalid=${this._invalid}
          ?required=${this.required}
        ></rtg-input>

        ${this._invalid
          ? html`
              <rtg-field-error
                id="${this.errorId}"
                part="${SbPhoneField.PARTS.error}"
                .errors=${[{ message: this._error }]}
              ></rtg-field-error>
            `
          : this.description
            ? html`
                <rtg-field-description
                  id="${this.descriptionId}"
                  part="${SbPhoneField.PARTS.description}"
                >
                  ${this.description}
                </rtg-field-description>
              `
            : nothing}
      </rtg-field>
    `;
  }
}
